import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    API_GATEWAY_PATH,
    AppHttpErrorResponse,
    BOOKWORKSHOP_SITE_IDS,
    CreateBookingRequestDto,
    ModelDto,
    OptionDto,
    RentalCarClass,
    ServiceDto,
    ShopDto,
    WorkshopCar,
} from '@impact/data';
import { EMPTY, Observable, Subject } from 'rxjs';
import { catchError, first, switchMap, takeUntil } from 'rxjs/operators';

import { IIndicataResponse } from '../indicata/indicata.models';
import { IBooking } from '../models/book-workshop.models';
import { getDateObjFromDate, getDateStringWithSeconds } from './helpers';
import { SettingsService, SiteIds } from './settings.service';
import { ToastService } from './toast.service';


@Injectable({
    providedIn: 'root',
})
export class WorkshopService {
    brandName = BOOKWORKSHOP_SITE_IDS.BilernesHus;
    private cancel$: Subject<void> = new Subject<void>();

    constructor(
        private http: HttpClient,
        private settingsService: SettingsService,
        private toastService: ToastService
    ) {
        this.settingsService
            .getSiteId()
            .pipe(first())
            .subscribe((siteID) => {
                switch (siteID) {
                    case SiteIds.BNDK:
                        // this.brandName = BOOKWORKSHOP_SITE_IDS.BN; // TODO: Should return this, but for testing, we're using bilernes hus data.
                        this.brandName = BOOKWORKSHOP_SITE_IDS.BilernesHus;
                        break;
                    case SiteIds.BHDK:
                        this.brandName = BOOKWORKSHOP_SITE_IDS.BilernesHus;
                        break;
                    default:
                        this.brandName = BOOKWORKSHOP_SITE_IDS.BN;
                        break;
                }
            });
    }

    getCarDetails(registrationNumber: string) {
        const url = `/${API_GATEWAY_PATH}/Workshop/GetCarDetails/${this.brandName}/${registrationNumber}`;
        return this.http.get<WorkshopCar>(url);
        // Do not use error handling here. It is handled in the caller method
    }
    getMakes() {
        const url = `/${API_GATEWAY_PATH}/Workshop/GetMakes/${this.brandName}`;
        return this.http.get<ModelDto[]>(url);
    }

    getModels(makeCode?: string | null) {
        const url = `/${API_GATEWAY_PATH}/Workshop/GetModels/${this.brandName}/${makeCode}`;
        return this.http.get<ModelDto[]>(url);
    }

    getShops() {
        const url = `/${API_GATEWAY_PATH}/Workshop/GetShops/${this.brandName}`;
        return this.http.get<ShopDto[]>(url).pipe(
            catchError((err: AppHttpErrorResponse) => {
                this.handleHttpError(err);
                return EMPTY;
            })
        );
    }

    getShopServices(booking: IBooking) {
        const shopId = booking?.shop?.dracarID ? booking?.shop?.dracarID : booking?.shop?.id;
        const url = `/${API_GATEWAY_PATH}/Workshop/GetShopServices/${this.brandName}/${shopId}/${booking.makeCode}/${booking.modelCode}/${booking.variantCode}`;
        return this.http.get<ServiceDto[]>(url).pipe(
            catchError((err: AppHttpErrorResponse) => {
                this.handleHttpError(err);
                return EMPTY;
            })
        );
    }

    getBookingOptions(booking: IBooking) {
        const shopId = booking?.shop?.dracarID ? booking?.shop?.dracarID : booking?.shop?.id;
        const url = `/${API_GATEWAY_PATH}/Workshop/GetBookingOptions/${this.brandName}/${shopId}/${booking.makeCode}/${booking.modelCode}/${booking.variantCode}`;
        return this.http.get<OptionDto[]>(url).pipe(
            catchError((err: AppHttpErrorResponse) => {
                this.handleHttpError(err);
                return EMPTY;
            })
        );
    }

    getAvailableRentalCarClasses(booking: IBooking) {
        const rentCarBranchOfficeId = booking?.shop?.rentlogID;
        const url = `/${API_GATEWAY_PATH}/Workshop/GetAvailableRentalCarClasses/${rentCarBranchOfficeId}`;
        return this.http.get<RentalCarClass[]>(url).pipe(takeUntil(this.cancel$));
    }

    getBookingDatesWithRental(booking: IBooking, CarClassId: number) {
        this.cancel$.next();
        const shopId = booking?.shop?.dracarID ? booking?.shop?.dracarID : booking?.shop?.id;
        const jobCodes = booking?.services?.map((service) => service.code).join();
        const rentCarBranchOfficeId = booking?.shop?.rentlogID;
        const url = `/${API_GATEWAY_PATH}/Workshop/GetBookingDates/${this.brandName}/${shopId}/${booking.makeCode}/${booking.modelCode}/${booking.variantCode}/${booking.regNumber}/${booking.optionId}/${jobCodes}/${rentCarBranchOfficeId}/${CarClassId}`;

        return this.http.get<string[]>(url).pipe(takeUntil(this.cancel$));
    }

    getBookingDates(booking: IBooking) {
        this.cancel$.next();
        const shopId = booking?.shop?.dracarID ? booking?.shop?.dracarID : booking?.shop?.id;
        const jobCodes = booking?.services?.map((service) => service.code).join();
        const url = `/${API_GATEWAY_PATH}/Workshop/GetBookingDates/${this.brandName}/${shopId}/${booking.makeCode}/${booking.modelCode}/${booking.variantCode}/${booking.regNumber}/${booking.optionId}/${jobCodes}`;

        return this.http.get<string[]>(url).pipe(
            catchError((err: AppHttpErrorResponse) => {
                this.handleHttpError(err);
                return EMPTY;
            }),
            takeUntil(this.cancel$)
        );
    }

    getBookingTimes(booking: IBooking, date: string) {
        this.cancel$.next();
        const jobCodes = booking?.services?.map((service) => service.code).join();
        const shopId = booking?.shop?.dracarID ? booking?.shop?.dracarID : booking?.shop?.id;
        const url = `/${API_GATEWAY_PATH}/Workshop/GetBookingTimes/${this.brandName}/${date}/${shopId}/${booking.makeCode}/${booking.modelCode}/${booking.variantCode}/${booking.regNumber}/${booking.optionId}/${jobCodes}`;

        return this.http.get<string[]>(url).pipe(takeUntil(this.cancel$));
    }

    getCarDataFromIndicataResponse({ make, model, providerData }: IIndicataResponse) {
        function getValue(typeName: string, keyName: string) {
            return providerData?.providerGroups?.find(({ type }) => type === typeName)?.entries?.find(({ key }) => key === keyName)?.value;
        }

        return {
            chassisNumber: getValue('Vehicle', 'VehicleIdentificationNumber'),
            modelDescription: `${make?.summary} ${model?.summary} ${getValue('Vehicle', 'Variant')}`,
            registrationDate: getDateStringWithSeconds(getDateObjFromDate(new Date(getValue('Registration', 'FirstRegistrationDate') || ''))),
        };
    }

    getCarDataFromDracarResponse({ variant, vin, registrationDate }: WorkshopCar) {
        return {
            chassisNumber: vin ?? undefined,
            modelDescription: variant ?? undefined,
            registrationDate: registrationDate ?? undefined,
        };
    }

    createBooking({
        address,
        makeCode,
        date,
        timeCode,
        email,
        firstName,
        lastName,
        mileage,
        modelCode,
        optionId,
        phone,
        pickupData,
        regNumber,
        services,
        shop,
        variantCode,
        zipCode,
        city,
        carIndicataData,
        carDracarData,
        comment,
        rentalCar,
        rentalCarClassName
    }: IBooking) {
        const providerData = carIndicataData
            ? this.getCarDataFromIndicataResponse(carIndicataData)
            : carDracarData
            ? this.getCarDataFromDracarResponse(carDracarData)
            : undefined;

        const rentalBranchOfficeId = Number(shop?.rentlogID);

        const body: CreateBookingRequestDto = {
            brandName: this.brandName,
            comment,
            date,
            timeCode,
            jobCodes: services?.map(({ code }) => code || '').filter((code) => code),
            makeCode,
            mileage,
            modelCode,
            optionId,
            personalInfo: {
                address,
                city,
                email,
                firstName,
                lastName,
                phoneNumber: phone,
                zipCode,
            },
            pickupData,
            registrationNumber: regNumber,
            shopCode: shop?.dracarID ? shop?.dracarID : shop?.id,
            variantCode,
            rentalCar,
            rentalCarClassName,
            rentalBranchOfficeId,
            ...providerData,
        };

        const createBookingUrl = `/${API_GATEWAY_PATH}/Workshop/CreateBooking`;
        const registerCustomerUrl = `/${API_GATEWAY_PATH}/Workshop/RegisterCustomer`;
        const addCustomerVehicleUrl = `/${API_GATEWAY_PATH}/Workshop/AddCustomerVehicle`;

        return this.http
            .post(registerCustomerUrl, {
                brandName: this.brandName,
                city,
                email,
                firstName,
                lastName,
                address,
                zipCode,
                phoneNumber: phone,
            })
            .pipe(
                switchMap(() =>
                    this.http.post(addCustomerVehicleUrl, {
                        brandName: this.brandName,
                        city,
                        email,
                        makeCode,
                        modelCode,
                        variantCode,
                        registrationNumber: regNumber,
                        registrationDate: providerData?.registrationDate
                    })
                ),
                switchMap(() => this.http.post(createBookingUrl, body))
            );
    }

    private handleHttpError(error: AppHttpErrorResponse): void {
        console.warn('Error happened', error);
        this.toastService.show(`Error ${error.error.status}: ${error.error.statusText}`);
    }
}
